import type { CSSProperties } from 'react'

/**
 * The docked AI Analyst panel's saved state: open, width and the current
 * conversation.
 *
 * It is a preference, not view state: the panel lives on the pathless `_app`
 * layout, which cannot carry a search schema (CLAUDE.md §3.3), and it follows
 * the person from page to page. So it goes to localStorage under
 * `aiAnalyst.panel`, wrapped in try/catch like theme and density. The
 * linkable view of a conversation is `/ai-analyst/c/$conversationId`.
 *
 * Who writes what: `AiAnalystPanel` writes `width` whenever it is resized; the
 * shell that mounts the panel writes `open` and `conversationId`.
 */

export interface PanelPreference {
  open: boolean
  width: number
  conversationId: string | null
}

export const PANEL_STORAGE_KEY = 'aiAnalyst.panel'
export const PANEL_MIN_WIDTH = 360
export const PANEL_MAX_WIDTH = 760
export const PANEL_DEFAULT_WIDTH = 420
/** One arrow key press on the resize handle. */
export const PANEL_WIDTH_STEP = 24
/** The panel never takes more than this share of the window. */
export const PANEL_MAX_VIEWPORT_SHARE = 0.7

/** The CSS custom property carrying the panel's width, for the shell's reserved padding. */
export const PANEL_WIDTH_VAR = '--ai-analyst-panel-width'

/**
 * Reserves the panel's width on the page beside it (put it on `SidebarInset`
 * with `panelReserveStyle`). From `md` up only: below that the panel is a sheet
 * over the page and pushes nothing.
 */
export const PANEL_RESERVE_CLASS = 'md:pr-(--ai-analyst-panel-width)'

export const DEFAULT_PANEL_PREFERENCE: PanelPreference = {
  open: false,
  width: PANEL_DEFAULT_WIDTH,
  conversationId: null,
}

/** The widest the panel may be in a window this wide: 760px, or 70% of the window. */
export function maxWidthFor(viewportWidth: number): number {
  return Math.max(
    PANEL_MIN_WIDTH,
    Math.min(PANEL_MAX_WIDTH, Math.floor(viewportWidth * PANEL_MAX_VIEWPORT_SHARE)),
  )
}

/** Keep a width inside 360..min(760, 70% of the window). */
export function clampWidth(width: number, viewportWidth: number): number {
  if (!Number.isFinite(width)) return PANEL_DEFAULT_WIDTH
  return Math.round(Math.min(maxWidthFor(viewportWidth), Math.max(PANEL_MIN_WIDTH, width)))
}

/** The saved state, field by field: a missing or malformed field takes its default. */
export function readPanelPreference(): PanelPreference {
  try {
    const raw = window.localStorage.getItem(PANEL_STORAGE_KEY)
    if (!raw) return DEFAULT_PANEL_PREFERENCE
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_PANEL_PREFERENCE
    const { open, width, conversationId } = parsed as Record<string, unknown>
    return {
      open: typeof open === 'boolean' ? open : DEFAULT_PANEL_PREFERENCE.open,
      width:
        typeof width === 'number' && Number.isFinite(width)
          ? Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, width))
          : DEFAULT_PANEL_PREFERENCE.width,
      conversationId: typeof conversationId === 'string' && conversationId ? conversationId : null,
    }
  } catch {
    // Private mode or blocked storage: start from the defaults.
    return DEFAULT_PANEL_PREFERENCE
  }
}

/** Merge `next` into the saved state. A blocked storage costs only the preference. */
export function writePanelPreference(next: Partial<PanelPreference>): void {
  try {
    window.localStorage.setItem(
      PANEL_STORAGE_KEY,
      JSON.stringify({ ...readPanelPreference(), ...next }),
    )
  } catch {
    // Storage is full or blocked; the panel still works for this visit.
  }
}

/**
 * The style that sets the reserved width, used with `PANEL_RESERVE_CLASS`.
 * Zero while the panel is closed, so the page gets its full width back.
 */
export function panelReserveStyle(open: boolean, width: number): CSSProperties {
  // The one allowed inline style (CLAUDE.md §5.1): a CSS custom property,
  // which a Tailwind class cannot set from a runtime value.
  return { [PANEL_WIDTH_VAR]: open ? `${width}px` : '0px' } as CSSProperties
}
